import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils';
import {
    financialDocumentFormSchema,
    FinancialDocumentFormSchemaValues,
} from 'src/features/financialDocuments/utils/formSchema';
import FinancialDocumentForm from 'src/features/financialDocuments/components/forms/FinancialDocumentForm';
import { useCreateFinancialDocument } from 'src/features/financialDocuments/api/useCreateFinancialDocument';
import { queryClient } from 'src/lib/react-query';
import { LIST_FINANCIAL_DOCUMENTS_RESOURCE } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { toast } from 'src/hooks/useToast';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useNavigate, useParams } from 'react-router-dom';

interface FinancialDocumentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateFinancialDocumentFormModal<TData>({
    children,
    className,
    semantics,
}: FinancialDocumentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { customerId = '', rangeId } = useParams();
    const navigate = useNavigate();

    const onError = useOnError();

    const { mutate, isPending } = useCreateFinancialDocument({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENTS_RESOURCE] });
            setIsModalOpen(false);
            navigate(`/financial-documents/${success.id}`);
            toast({
                title: formatMessage(financialDocumentsMessages.createFinancialDocumentSuccess),
                description: formatMessage(financialDocumentsMessages.createFinancialDocumentSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, form.setError);
        },
    });

    const defaultValues = React.useMemo(() => {
        return {
            type: undefined,
            accounting_range: rangeId ?? undefined,
            currency: undefined,
            public_description: undefined,
            internal_description: undefined,
            tax_event_date: undefined,
            payment_due_date: undefined,
            issue_due_date: undefined,
            supplier: undefined,
            customer: customerId ?? undefined,
            financial_document: undefined,
            number: undefined,
        };
    }, [customerId, rangeId]);

    const form = useForm<FinancialDocumentFormSchemaValues>({
        resolver: zodResolver(financialDocumentFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues, isModalOpen]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: FinancialDocumentFormSchemaValues) => {
            mutate(values);
        },

        [mutate],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.ADD} />
                        <div>
                            <DialogTitle>
                                {formatMessage(generalMessages.createEntry, {
                                    entry: semantics?.entryName?.toLowerCase(),
                                })}
                            </DialogTitle>
                            <DialogDescription>
                                {formatMessage(financialDocumentsMessages.addFinancialDocumentSubtitle)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <FinancialDocumentForm form={form} onSubmit={onSubmit} isEditMode={false} isDraft={false} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(generalMessages.createEntry, {
                            entry: semantics?.entryName?.toLowerCase(),
                        })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateFinancialDocumentFormModal;
